/**
 * Dehla Pakad (Mendicot / Mindi) — server-authoritative rules engine (North Indian partnership trick-taking, 4 players).
 *
 * Rules implemented (toggles live in {@link DehlaPakadConfig}):
 *  - 4 players in two partnerships: seats 0 & 2 (team 0) against seats 1 & 3 (team 1). One 52-card deck, ace high.
 *  - Each hand starts with five cards each. The seat after the dealer (the trump caller) looks at those five cards and
 *    names the trump suit; only then are the remaining eight cards each dealt. The trump caller leads the first trick.
 *  - Players must follow the led suit when able; a void player may play anything (trump or not).
 *  - Trick winner: highest trump, else highest card of the led suit. The winner takes the four cards and leads next.
 *  - The prize is the four tens ("dehla"). At the end of the hand each team counts the tens it captured:
 *      - one team took all 13 tricks ("Kot", a whitewash) — 3 match points;
 *      - one team took all four tens ("Mendicot") — 2 match points;
 *      - one team took three tens — 1 match point;
 *      - tens split 2–2 — the team with more tricks (7 or more) takes 1 match point.
 *  - The match ends when a team reaches `targetScore` and leads, or after `maxRounds` hands.
 *    Partners share a place, so payouts are split between the two partners.
 */
import { buildDeck, parseCard, removeCards, sortHand, SUITS, type CardId, type Suit } from '../../core/cards.js';
import { checkInt, mergeKnown } from '../../core/config.js';
import { shuffle, type Rng } from '../../core/rng.js';
import {
  assertEngine,
  EngineError,
  isPlainObject,
  type EngineEvent,
  type GameEngine,
  type Standing,
} from '../../core/types.js';

export interface DehlaPakadConfig {
  /** Match ends when a team reaches this many match points and leads. */
  targetScore: number;
  /** Hard cap on hands so a match always terminates. */
  maxRounds: number;
  roundEndDelayMs: number;
  trickEndDelayMs: number;
}

export type DehlaPakadPhase = 'TRUMP' | 'PLAYING' | 'TRICK_END' | 'ROUND_END' | 'MATCH_END';

export interface DehlaPakadTrickPlay {
  seat: number;
  card: CardId;
}

export interface DehlaPakadCompletedTrick {
  leader: number;
  plays: DehlaPakadTrickPlay[];
  winner: number;
}

export type DehlaPakadOutcome = 'KOT' | 'MENDICOT' | 'TENS' | 'TRICKS';

/** Per-hand result for one team (public once the hand ends). */
export interface DehlaPakadTeamRound {
  tens: number;
  tricks: number;
  /** Match points scored this hand. */
  delta: number;
  /** The hand's outcome, set on both rows (the winning team has delta > 0). */
  outcome: DehlaPakadOutcome;
}

export interface DehlaPakadState {
  config: DehlaPakadConfig;
  players: string[];
  round: number;
  dealer: number;
  phase: DehlaPakadPhase;
  hands: CardId[][];
  /** The 32 cards still to be dealt once trump is named (never exposed in views). */
  stock: CardId[];
  trump: Suit | null;
  tricksWon: number[];
  /** Tens captured per team. */
  tensWon: number[];
  turn: number;
  trick: DehlaPakadTrickPlay[];
  tricks: DehlaPakadCompletedTrick[];
  teamScores: number[];
  roundResults: DehlaPakadTeamRound[][];
}

export type DehlaPakadAction = { type: 'trump'; suit: Suit } | { type: 'play'; card: CardId };

export type DehlaPakadLegal = { kind: 'trump'; suits: Suit[] } | { kind: 'play'; cards: CardId[] } | { kind: 'none' };

export interface DehlaPakadView {
  gameType: 'DEHLA_PAKAD';
  config: DehlaPakadConfig;
  seat: number | null;
  phase: DehlaPakadPhase;
  round: number;
  dealer: number;
  turn: number;
  /** Seat that names trump and leads the first trick. */
  caller: number;
  hand: CardId[];
  handCounts: number[];
  trump: Suit | null;
  tricksWon: number[];
  /** Tens captured by each team so far (team 0 = seats 0 & 2). */
  tensWon: number[];
  trick: DehlaPakadTrickPlay[];
  lastTrick: DehlaPakadCompletedTrick | null;
  tricks: DehlaPakadCompletedTrick[];
  teamScores: number[];
  roundResults: DehlaPakadTeamRound[][];
  /** Score of each seat's team (so generic score UIs work). */
  totals: number[];
}

const DEFAULTS: DehlaPakadConfig = {
  targetScore: 5,
  maxRounds: 15,
  roundEndDelayMs: 5000,
  trickEndDelayMs: 900,
};

const SEATS = 4;
const FIRST_DEAL = 5;

export const dehlaPakadTeamOf = (seat: number): number => seat % 2;
export const dehlaPakadPartnerOf = (seat: number): number => (seat + 2) % SEATS;

export function dehlaPakadTrickWinner(plays: readonly DehlaPakadTrickPlay[], trump: Suit): number {
  const lead = parseCard(plays[0]!.card).suit;
  let best = plays[0]!;
  for (const p of plays.slice(1)) {
    const c = parseCard(p.card);
    const b = parseCard(best.card);
    const beats = (c.suit === trump && b.suit !== trump) || (c.suit === b.suit && c.rank > b.rank);
    if (beats && (c.suit === trump || c.suit === lead)) best = p;
  }
  return best.seat;
}

export function dehlaPakadLegalCards(hand: readonly CardId[], trick: readonly DehlaPakadTrickPlay[]): CardId[] {
  if (trick.length === 0) return [...hand];
  const lead = parseCard(trick[0]!.card).suit;
  const followers = hand.filter((c) => parseCard(c).suit === lead);
  return followers.length > 0 ? followers : [...hand];
}

export const dehlaPakadIsTen = (card: CardId): boolean => parseCard(card).rank === 10;

/** Match points for a finished hand: [team 0, team 1], plus how the hand was decided. */
export function dehlaPakadScoreRound(tens: readonly number[], tricks: readonly number[]): { points: [number, number]; outcome: DehlaPakadOutcome } {
  const points: [number, number] = [0, 0];
  const kot: number = tricks[0] === 13 ? 0 : tricks[1] === 13 ? 1 : -1;
  if (kot >= 0) {
    points[kot] = 3;
    return { points, outcome: 'KOT' };
  }
  if (tens[0] === 4 || tens[1] === 4) {
    points[tens[0] === 4 ? 0 : 1] = 2;
    return { points, outcome: 'MENDICOT' };
  }
  if (tens[0] === 3 || tens[1] === 3) {
    points[tens[0] === 3 ? 0 : 1] = 1;
    return { points, outcome: 'TENS' };
  }
  points[tricks[0]! > tricks[1]! ? 0 : 1] = 1;
  return { points, outcome: 'TRICKS' };
}

/** Suit the player holds most strongly in their first five cards (length first, then high cards and tens). */
export function dehlaPakadBestSuit(hand: readonly CardId[]): Suit {
  let best: Suit = SUITS[0]!;
  let bestScore = -1;
  for (const suit of SUITS) {
    const ranks = hand.map(parseCard).filter((c) => c.suit === suit).map((c) => c.rank);
    const score = ranks.length * 10 + ranks.reduce((n, r) => n + (r >= 11 ? r - 9 : 0), 0);
    if (score > bestScore) {
      bestScore = score;
      best = suit;
    }
  }
  return best;
}

function deal(state: DehlaPakadState, rng: Rng, events: EngineEvent[]): void {
  const deck = shuffle(buildDeck(), rng);
  const hands: CardId[][] = [[], [], [], []];
  for (let i = 0; i < FIRST_DEAL * SEATS; i++) hands[i % SEATS]!.push(deck[i]!);
  state.hands = hands.map((h) => sortHand(h));
  state.stock = deck.slice(FIRST_DEAL * SEATS);
  state.trump = null;
  state.tricksWon = [0, 0, 0, 0];
  state.tensWon = [0, 0];
  state.trick = [];
  state.tricks = [];
  state.phase = 'TRUMP';
  state.turn = (state.dealer + 1) % SEATS;
  events.push({ type: 'dealt', round: state.round, dealer: state.dealer, counts: state.hands.map((h) => h.length) });
}

export const dehlaPakadEngine: GameEngine<DehlaPakadConfig, DehlaPakadState, DehlaPakadAction, DehlaPakadView, DehlaPakadLegal> = {
  gameType: 'DEHLA_PAKAD',
  kind: 'match',

  defaultConfig: () => ({ ...DEFAULTS }),

  normalizeConfig(input) {
    const c = mergeKnown(DEFAULTS, input);
    checkInt('targetScore', c.targetScore, 1, 50);
    checkInt('maxRounds', c.maxRounds, 1, 40);
    checkInt('roundEndDelayMs', c.roundEndDelayMs, 0, 60000);
    checkInt('trickEndDelayMs', c.trickEndDelayMs, 0, 10000);
    return c;
  },

  seatLimits: () => ({ min: SEATS, max: SEATS }),

  createState(config, seats, rng) {
    assertEngine(seats.length === SEATS, 'INVALID_CONFIG', 'Dehla Pakad needs exactly 4 seats');
    const players = Array<string>(SEATS).fill('');
    for (const s of seats) {
      assertEngine(s.seat >= 0 && s.seat < SEATS && !players[s.seat], 'SEAT_TAKEN', `Bad seat ${s.seat}`);
      players[s.seat] = s.playerId;
    }
    const state: DehlaPakadState = {
      config,
      players,
      round: 1,
      dealer: rng.int(SEATS),
      phase: 'TRUMP',
      hands: [],
      stock: [],
      trump: null,
      tricksWon: [],
      tensWon: [],
      turn: 0,
      trick: [],
      tricks: [],
      teamScores: [0, 0],
      roundResults: [],
    };
    deal(state, rng, []);
    return state;
  },

  parseAction(raw) {
    if (!isPlainObject(raw)) throw new EngineError('INVALID_ACTION', 'Action must be an object');
    if (raw.type === 'trump' && typeof raw.suit === 'string' && (SUITS as readonly string[]).includes(raw.suit)) return { type: 'trump', suit: raw.suit as Suit };
    if (raw.type === 'play' && typeof raw.card === 'string' && raw.card.length <= 4) return { type: 'play', card: raw.card };
    throw new EngineError('INVALID_ACTION', 'Unknown Dehla Pakad action');
  },

  applyAction(state, seat, action) {
    assertEngine(state.phase !== 'MATCH_END', 'GAME_OVER', 'Match is over');
    assertEngine(seat === state.turn && (state.phase === 'TRUMP' || state.phase === 'PLAYING'), 'NOT_YOUR_TURN', 'Not your turn');
    const events: EngineEvent[] = [];

    if (action.type === 'trump') {
      assertEngine(state.phase === 'TRUMP', 'INVALID_PHASE', 'Trump is not being chosen');
      state.trump = action.suit;
      state.stock.forEach((c, i) => state.hands[i % SEATS]!.push(c));
      state.stock = [];
      state.hands = state.hands.map((h) => sortHand(h));
      state.phase = 'PLAYING';
      state.turn = seat;
      events.push({ type: 'trumpChosen', seat, suit: action.suit });
      events.push({ type: 'dealtRest', counts: state.hands.map((h) => h.length) });
      return events;
    }

    assertEngine(action.type === 'play' && state.phase === 'PLAYING', 'INVALID_PHASE', 'Not in play phase');
    const hand = state.hands[seat]!;
    assertEngine(hand.includes(action.card), 'INVALID_CARD', 'Card not in hand');
    assertEngine(dehlaPakadLegalCards(hand, state.trick).includes(action.card), 'ILLEGAL_MOVE', 'That card is not allowed now');
    removeCards(hand, [action.card]);
    state.trick.push({ seat, card: action.card });
    events.push({ type: 'cardPlayed', seat, card: action.card });
    if (state.trick.length < SEATS) {
      state.turn = (seat + 1) % SEATS;
      return events;
    }
    const winner = dehlaPakadTrickWinner(state.trick, state.trump!);
    const tens = state.trick.filter((p) => dehlaPakadIsTen(p.card)).length;
    state.tricksWon[winner]!++;
    state.tensWon[dehlaPakadTeamOf(winner)]! += tens;
    state.tricks.push({ leader: state.trick[0]!.seat, plays: state.trick, winner });
    events.push({ type: 'trickWon', seat: winner, trickNo: state.tricks.length, tens });
    state.phase = 'TRICK_END';
    state.turn = winner;
    return events;
  },

  getActors(state) {
    return state.phase === 'TRUMP' || state.phase === 'PLAYING' ? [state.turn] : [];
  },

  getLegalActions(state, seat) {
    if (seat !== state.turn) return { kind: 'none' };
    if (state.phase === 'TRUMP') return { kind: 'trump', suits: [...SUITS] };
    if (state.phase === 'PLAYING') return { kind: 'play', cards: dehlaPakadLegalCards(state.hands[seat]!, state.trick) };
    return { kind: 'none' };
  },

  getTimeoutAction(state, seat) {
    if (state.phase === 'TRUMP') return { type: 'trump', suit: dehlaPakadBestSuit(state.hands[seat]!) };
    const legal = dehlaPakadLegalCards(state.hands[seat]!, state.trick);
    const trump = state.trump!;
    // Lowest legal card, keeping trumps and tens for last: rarely wastes a winner, never illegal.
    const weight = (id: CardId) => {
      const c = parseCard(id);
      return (c.suit === trump ? 100 : 0) + (c.rank === 10 ? 20 : 0) + c.rank;
    };
    const lowest = [...legal].sort((a, b) => weight(a) - weight(b))[0]!;
    return { type: 'play', card: lowest };
  },

  getView(state, seat) {
    return {
      gameType: 'DEHLA_PAKAD',
      config: state.config,
      seat,
      phase: state.phase,
      round: state.round,
      dealer: state.dealer,
      turn: state.turn,
      caller: (state.dealer + 1) % SEATS,
      hand: seat === null ? [] : [...state.hands[seat]!],
      handCounts: state.hands.map((h) => h.length),
      trump: state.trump,
      tricksWon: [...state.tricksWon],
      tensWon: [...state.tensWon],
      trick: state.phase === 'TRICK_END' ? [...(state.tricks.at(-1)?.plays ?? [])] : [...state.trick],
      lastTrick: state.tricks.at(-1) ?? null,
      tricks: state.tricks.map((t) => ({ ...t, plays: [...t.plays] })),
      teamScores: [...state.teamScores],
      roundResults: state.roundResults.map((r) => r.map((x) => ({ ...x }))),
      totals: [0, 1, 2, 3].map((s) => state.teamScores[dehlaPakadTeamOf(s)]!),
    };
  },

  getSystemStep(state) {
    if (state.phase === 'TRICK_END') return { kind: 'collectTrick', delayMs: state.config.trickEndDelayMs };
    if (state.phase === 'ROUND_END') return { kind: 'nextRound', delayMs: state.config.roundEndDelayMs };
    return null;
  },

  runSystemStep(state, rng) {
    const events: EngineEvent[] = [];
    if (state.phase === 'TRICK_END') {
      state.trick = [];
      if (state.tricks.length < 13) {
        state.phase = 'PLAYING';
        events.push({ type: 'trickCollected', leader: state.turn });
        return events;
      }
      const cfg = state.config;
      const tricks = [0, 1].map((team) => state.tricksWon[team]! + state.tricksWon[team + 2]!);
      const { points, outcome } = dehlaPakadScoreRound(state.tensWon, tricks);
      const results: DehlaPakadTeamRound[] = [0, 1].map((team) => ({ tens: state.tensWon[team]!, tricks: tricks[team]!, delta: points[team]!, outcome }));
      results.forEach((r, team) => {
        state.teamScores[team]! += r.delta;
      });
      state.roundResults.push(results);
      events.push({ type: 'roundEnd', round: state.round, results, teamScores: [...state.teamScores], tricksWon: [...state.tricksWon] });
      const [a, b] = state.teamScores as [number, number];
      const reached = a >= cfg.targetScore || b >= cfg.targetScore;
      if ((reached && a !== b) || state.round >= cfg.maxRounds) {
        state.phase = 'MATCH_END';
        events.push({ type: 'matchEnd', standings: teamStandings(state) });
      } else {
        state.phase = 'ROUND_END';
      }
      return events;
    }
    if (state.phase === 'ROUND_END') {
      state.round++;
      state.dealer = (state.dealer + 1) % SEATS;
      deal(state, rng, events);
      return events;
    }
    return events;
  },

  isFinished: (state) => state.phase === 'MATCH_END',

  getStandings(state): Standing[] {
    return teamStandings(state);
  },

  getRoundsCompleted: (state) => state.roundResults.length,
};

/** Partners share a place: the leading team is 1st, the other 2nd (both 1st on a tie). */
function teamStandings(state: DehlaPakadState): Standing[] {
  const [a, b] = state.teamScores as [number, number];
  return [0, 1, 2, 3].map((seat) => {
    const team = dehlaPakadTeamOf(seat);
    const mine = team === 0 ? a : b;
    const theirs = team === 0 ? b : a;
    return { seat, score: mine, place: mine >= theirs ? 1 : 2 };
  });
}
